import { Control, Controller } from 'react-hook-form';
import { FormInput, FormSelect } from '@/components/Common/FormFields';
import { CreateArrayOptions } from '@/constants';
import { Link } from 'lucide-react';

type Option = {
  value: string | number;
  label: string;
  google_map_link?: string;
};

type TimingSectionProps = {
  control: Control<any>;
  isPaidInitial: boolean;
  pickupTimeOptions: Option[];
  hotels: Option[];
  cars: Option[];
  mountains: Option[];
  agents: Option[];
  lessonsEntry?: string;
};

const TimingSection: React.FC<TimingSectionProps> = ({
  control,
  isPaidInitial,
  pickupTimeOptions,
  hotels,
  cars,
  mountains,
  agents,
  lessonsEntry
}) => {
  return (
    <>
      <div className='overflow-x-auto'>
        <table
          className='table-fixed w-full border border-gray-300 rounded-xl text-sm text-center mx-auto mb-4'
          style={{ minWidth: '1250px' }}
        >
          <thead className='bg-blue-100 text-gray-800'>
            <tr>
              <th className='py-2 px-2.5 border'>Hotel</th>
              <th className='py-2 px-2.5 border'>Vehicle</th>
              <th className='py-2 px-2.5 border'>Pick-up numbers</th>
              <th className='py-2 px-2.5 border'>Pickup Time</th>
              <th className='py-2 px-2.5 border'>Mountain</th>
            </tr>
          </thead>

          <tbody className='bg-white divide-y divide-gray-200'>
            <tr className='bg-white hover:bg-gray-50'>
              <td className='border px-2 py-1.5 align-middle'>
                <FormSelect
                  name='hotel_id'
                  control={control}
                  options={hotels}
                  placeholder='Select Hotel'
                  disabled={isPaidInitial}
                  searchable
                />
                <Controller
                  name='hotel_id'
                  control={control}
                  render={({ field }) => {
                    const selected = hotels.find(
                      h => String(h.value) === String(field.value)
                    );
                    return (
                      <span className='block mt-1 text-xs text-slate-600'>
                        {selected?.google_map_link && (
                          <a
                            href={selected.google_map_link}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-blue-600 underline flex items-center justify-center gap-1'
                          >
                            Google Map Link
                            <Link size={15} />
                          </a>
                        )}
                      </span>
                    );
                  }}
                />
              </td>
              <td className='border px-2 py-1.5 align-middle'>
                <FormSelect
                  name='car_id'
                  control={control}
                  options={cars}
                  placeholder='Select Vehicle'
                  disabled={isPaidInitial}
                  searchable
                />
              </td>
              <td className='border px-2 py-1.5 align-middle'>
                <FormSelect
                  name='transport_pax'
                  control={control}
                  options={CreateArrayOptions(100, 'Select Pick-up numbers')}
                  placeholder='Select Pick-up numbers'
                  disabled={isPaidInitial}
                />
              </td>
              <td className='border px-2 py-1.5 align-middle'>
                <FormSelect
                  name='pickup_time'
                  control={control}
                  options={pickupTimeOptions}
                  placeholder='Select pickup time'
                  disabled={isPaidInitial}
                />
              </td>
              <td className='border px-2 py-1.5 align-middle'>
                <FormSelect
                  name='mountain_id'
                  control={control}
                  options={mountains}
                  placeholder='Select Mountain'
                  disabled={isPaidInitial}
                  searchable
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className='overflow-x-auto'>
        <table
          className='table-fixed w-full border border-gray-300 rounded-xl text-sm text-center mx-auto mb-4'
          style={{ minWidth: '500px' }}
        >
          <thead className='bg-blue-100 text-gray-800'>
            <tr>
              <th className='min-w-[160px] py-2 px-2.5 border'>Agent</th>
              <th className='min-w-[150px] py-2 px-2.5 border'>Ref</th>
              <th className='w-auto py-2 px-2.5 border'>Pro/Unpro</th>
            </tr>
          </thead>

          <tbody className='bg-white divide-y divide-gray-200'>
            <tr className='bg-white hover:bg-gray-50'>
              <td className='border px-2 py-1.5 align-middle'>
                <FormSelect
                  name='agent_id'
                  control={control}
                  options={agents}
                  placeholder='Select Agent'
                  disabled={isPaidInitial}
                  searchable
                />
              </td>
              <td className='border px-2 py-1.5 align-middle'>
                <FormInput
                  name='reference'
                  control={control}
                  label=''
                  disabled={isPaidInitial}
                />
              </td>
              <td className='border px-2 py-1.5 align-middle'>
                <Controller
                  name='paid'
                  control={control}
                  render={({ field }) => (
                    <label className='inline-flex items-center gap-2'>
                      <input
                        type='checkbox'
                        checked={field.value === 'Y' || lessonsEntry === 'Y'}
                        onChange={e =>
                          field.onChange(e.target.checked ? 'Y' : 'N')
                        }
                        disabled={isPaidInitial}
                        className='h-4 w-4 text-blue-600 border-gray-300 rounded'
                      />
                      <span className='text-sm text-slate-800'>Pro/Unpro</span>
                    </label>
                  )}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
};

export default TimingSection;
